import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Invoice, ProcessPaymentModel, ProcessPaymentsChecksModel, ProcessPaymentsParamModel } from '@app/interfaces';
import { PaymentSessionModel } from '@app/models/fund-management';
import { isNil } from '@app/utils';

@Injectable()
export class ProcessPaymentsService {
	endpointBase = '/api/process-payments';

	constructor(private readonly http: HttpClient) {}

	public getUnpaidItems(model: ProcessPaymentsParamModel): Observable<any> {
		return this.http.get(`${this.endpointBase}/get-unpaid`, {
			params: {
				BankId: !isNil(model.BankId) ? model.BankId.toString() : null,
				ProcessBy: model.ProcessBy,
				MethodOfPaymentId: model.MethodOfPaymentId.toString(),
				Source: !isNil(model.Source) ? model.Source : null,
				DueOnOrBefore: model.DueOnOrBefore.toISOString(),
			},
		});
	}

	public getCurrentApProcessingInfo(sessionId: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/process-payments/${sessionId}`);
	}

	public getFundAchLog(transactionGuid: string): Observable<any> {
		return this.http.get(`${this.endpointBase}/get-fund-ach-log`, { params: { transactionGuid: isNil(transactionGuid) ? '' : transactionGuid } });
	}

	public queueProcessPayments(paramsModel: ProcessPaymentModel): Observable<any> {
		return this.http.post(`${this.endpointBase}/queue-process-payments`, paramsModel);
	}

	public cancelPaymentSession(session: PaymentSessionModel): Observable<any> {
		return this.http.post(`${this.endpointBase}/cancel-payment-session`, session);
	}

	public getPaymentSession(sessionId: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/get-payment-session/${sessionId}`);
	}

	public acceptAndRegisterChecks(session: PaymentSessionModel): Observable<any> {
		return this.http.post(`${this.endpointBase}/accept-and-register-checks`, session);
	}

	public getChecksToBePrintedReport(paramsModel: ProcessPaymentModel): Observable<any> {
		const headers = new HttpHeaders({ 'Content-Type': 'application/json', responseType: 'blob', Accept: 'application/pdf' });
		return this.http.get(`${this.endpointBase}/get-checks-to-be-printed-report`, { params: paramsModel, headers, responseType: 'blob' });
	}

	public getChecksToBePrintedOverflowReport(sessionId: number): Observable<any> {
		const headers = new HttpHeaders({ 'Content-Type': 'application/json', responseType: 'blob', Accept: 'application/pdf' });
		return this.http.get(`${this.endpointBase}/get-checks-to-be-printed-overflow-report/${sessionId}`, { headers, responseType: 'blob' });
	}

	public queueGenerateApChecksForPrinting(paramsModel: ProcessPaymentsChecksModel): Observable<any> {
		return this.http.post(`${this.endpointBase}/generate-ap-checks-for-printing`, paramsModel);
	}

	public getApChecksForPrinting(paramsModel: ProcessPaymentsChecksModel): Observable<any> {
		let params = new HttpParams();
		params = params.set('BankId', paramsModel.BankId);
		params = params.set('FM_HorizontalAdjustment', paramsModel.FM_HorizontalAdjustment);
		params = params.set('FM_VerticalAdjustment', paramsModel.FM_VerticalAdjustment);
		params = params.set('FM_MicrHorizontalAdjustment', paramsModel.FM_MicrHorizontalAdjustment);
		params = params.set('FM_MicrVerticalAdjustment', paramsModel.FM_MicrVerticalAdjustment);
		if (!isNil(paramsModel.SessionId)) {
			params = params.set('SessionId', paramsModel.SessionId);
		}

		const headers = new HttpHeaders({ 'Content-Type': 'application/json', responseType: 'blob', Accept: 'application/pdf' });
		return this.http.get(`${this.endpointBase}/get-ap-checks-for-printing`, {
			headers,
			params,
			responseType: 'blob',
		});
	}

	public acceptAndRegisterAch(sessionId: number): Observable<any> {
		return this.http.post(`${this.endpointBase}/accept-and-register-ach`, sessionId);
	}

	public queueAchReport(transactionGuid: string): Observable<any> {
		return this.http.post(`${this.endpointBase}/queue-ach-report`, JSON.stringify(transactionGuid), {
			headers: { 'Content-Type': 'application/json' },
		});
	}

	public queueAchArchivedFile(transactionGuid: string): Observable<any> {
		return this.http.post(`${this.endpointBase}/queue-ach-archived-file`, JSON.stringify(transactionGuid), {
			headers: { 'Content-Type': 'application/json' },
		});
	}

	public acceptAndRegisterEft(sessionId: number): Observable<any> {
		return this.http.post(`${this.endpointBase}/accept-and-register-eft`, sessionId);
	}

	public createPaymentInvoices(invoices: Invoice[]): Observable<any> {
		return this.http.post(`${this.endpointBase}/create-payment-invoices`, invoices);
	}
}
